<template>
  <AlCard class="month-card" data-testid="monthly-chart">
    <div class="month-card-header">
      <h2 v-if="isChartView">
        <Localization message="monthly-summary-chart" />
      </h2>
      <AlButton
        is-ghost
        v-if="isChartView"
        data-testid="month-day-toggle"
        id="view-month-table"
        class="toggle-month-details"
        @click="view = 'table'"
        v-featureflow:isOn="'capacity-planner-workload-table'"
      >
        <i class="fas fa-grip-horizontal"></i>
      </AlButton>

      <h2 class="month-card-heading" v-if="isTableView">
        <Localization message="monthly-summary-table" />
        <Localization
          v-if="hasSelectedDay"
          message="monthly-summary-table-daily"
        />
      </h2>
      <AlButton
        is-ghost
        v-if="isTableView"
        @click="view = 'chart'"
        data-testid="month-day-toggle"
        id="view-month-chart"
        class="toggle-month-details"
      >
        <i class="fas fa-chart-area"></i>
      </AlButton>
    </div>
    <MonthChart
      :month-data="monthData"
      :month="month"
      :unitOfWork="unitOfWork"
      :loading="loading"
      v-if="isChartView"
    />
    <MonthTable
      :month-data="monthData"
      :month="month"
      :unitOfWork="unitOfWork"
      :timezone="timezone"
      :loading="loading"
      :workloadTasks="workloadTasks"
      :configurationId="configurationId"
      v-if="isTableView"
      @day-selected="onDaySelect"
    />
  </AlCard>
</template>

<script>
import MonthChart from "./MonthChart.vue";
import MonthTable from "./MonthTable.vue";
import {
  Localization,
  CapacityPlannerMonthlyView,
  WorkloadTasks,
} from "@agyletime/wfm-vue-common";
import { AlCard, AlButton } from "@playvox/almendra-components";

export default {
  name: "MonthChartCard",
  components: {
    Localization,
    MonthChart,
    MonthTable,
    AlCard,
    AlButton,
  },
  props: {
    configurationId: {
      type: String,
      required: true,
    },
    timezone: {
      type: String,
      required: true,
    },
    month: {
      type: Date,
      required: true,
    },
    unitOfWork: {
      type: String,
      required: true,
    },
    status: {
      type: String,
      required: true,
    },
  },
  watch: {
    async month() {
      await this.getDailyCapacityForMonth();
      this.hydrateCapacityWithTasks();
    },
    async status() {
      await this.getDailyCapacityForMonth();
      this.hydrateCapacityWithTasks();
    },
  },
  async created() {
    await Promise.all([
      this.getDailyCapacityForMonth(),
      this.getWorkloadTasks(),
    ]);
    this.hydrateCapacityWithTasks();
  },
  data() {
    return {
      monthData: [],
      workloadTasks: new Map(),
      loading: true,
      view: "chart",
      selectedDay: null,
    };
  },
  computed: {
    isChartView() {
      return this.view === "chart";
    },
    isTableView() {
      return this.view === "table";
    },
    hasSelectedDay() {
      return this.selectedDay !== null;
    },
  },
  methods: {
    onDaySelect(day) {
      this.selectedDay = day;
    },
    async getDailyCapacityForMonth() {
      this.loading = true;
      this.monthData = [];
      try {
        const result =
          await CapacityPlannerMonthlyView.getDailyCapacityForMonth(
            this.configurationId,
            this.month
          );
        this.monthData = result;
      } catch (error) {
        this.monthData = [];
      } finally {
        this.loading = false;
        window.dispatchEvent(new Event("resize")); // * Resize screen to fit chart and show x-axis correctly
      }
    },
    async getWorkloadTasks() {
      this.workloadTasks = {};
      try {
        const result = await WorkloadTasks.getWorkloadTasks();
        this.workloadTasks = result.reduce((acc, task) => {
          acc.set(task.id, task);
          return acc;
        }, new Map());
      } catch (error) {
        this.workloadTasks = {};
      }
    },
    hydrateCapacityWithTasks() {
      this.monthData = this.monthData.map((day) => {
        return {
          ...day,
          workloadTasks: day.workloadTasks?.map((task) => {
            return {
              ...task,
              task: this.workloadTasks.get(task.workloadTaskId),
            };
          }),
        };
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.month-card-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.month-card-heading {
  font-weight: normal;

  :last-child {
    font-weight: bold;
  }
}
</style>
